import type { Ornamento, OrnamentoDePapel, Paleta } from '@miolos/core';
import { PASSO_ORNAMENTO_DE_PAPEL_MM, ehOrnamentoDePapel } from '@miolos/core';

interface Props {
  tipo: Ornamento;
  paleta: Paleta;
}

/**
 * Papel decorado: o padrão que cobre a folha inteira, por baixo de tudo.
 *
 * Quatro jeitos de decorar **a folha** (e não um canto dela): papel pontilhado,
 * quadriculado leve, ondinhas e confete. O desenho é um SVG repetido como fundo
 * de uma camada absoluta — de propósito:
 *
 * - o padrão **não ocupa espaço no fluxo**, então não empurra nada e não corta
 *   conteúdo (a página mede exatamente o mesmo com e sem ele);
 * - fica em `zIndex: 0`, *antes* das imagens do cliente: quem enviou uma arte de
 *   fundo continua vendo a arte dela, não pontinhos por cima.
 *
 * A repetição sai de `PASSO_ORNAMENTO_DE_PAPEL_MM`, a mesma medida que o
 * assistente usa para explicar o padrão — uma régua só.
 */
export function PapelDecorado({ tipo, paleta }: Props) {
  if (!ehOrnamentoDePapel(tipo)) return null;

  const [larguraMm, alturaMm] = PASSO_ORNAMENTO_DE_PAPEL_MM[tipo];

  return (
    <div
      aria-hidden
      data-papel={tipo}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        backgroundImage: `url("${padrao(tipo, paleta)}")`,
        backgroundRepeat: 'repeat',
        backgroundSize: `${larguraMm}mm ${alturaMm}mm`,
        opacity: OPACIDADE[tipo],
      }}
    />
  );
}

/**
 * Opacidade do padrão inteiro. Baixa de propósito: o ornamento decora o papel,
 * não compete com a letra miúda dos quadros nem gasta tinta além do necessário.
 */
const OPACIDADE: Record<OrnamentoDePapel, number> = {
  PONTOS: 0.75,
  QUADRICULADO: 0.5,
  ONDAS: 0.55,
  CONFETE: 0.6,
};

/**
 * O desenho de uma repetição do padrão, em SVG.
 *
 * O `viewBox` está na mesma unidade do `backgroundSize` (1 unidade = 1 mm), então
 * o que está escrito aqui é o tamanho impresso. Os traços das bordas ficam
 * cortados pela metade na emenda — e é assim que a repetição fica contínua: a
 * borda que falta na peça de cima é a borda que a peça de baixo desenha.
 */
function padrao(tipo: OrnamentoDePapel, paleta: Paleta): string {
  const [largura, altura] = PASSO_ORNAMENTO_DE_PAPEL_MM[tipo];
  const linha = paleta.linha;
  const principal = paleta.principal;
  const destaque = paleta.destaque;
  const claro = paleta.principalClaro;

  const conteudo: Record<OrnamentoDePapel, string> = {
    PONTOS: `<circle cx="${largura / 2}" cy="${altura / 2}" r="0.32" fill="${linha}"/>`,

    QUADRICULADO:
      `<path d="M0 0 V${altura} M0 0 H${largura}" fill="none" stroke="${linha}" stroke-width="0.15"/>`,

    ONDAS:
      `<path d="M0 ${altura * 0.74} C ${largura * 0.17} ${altura * 0.48} ${largura * 0.33} ${altura * 0.48} ` +
      `${largura * 0.5} ${altura * 0.74} C ${largura * 0.67} ${altura} ${largura * 0.83} ${altura} ${largura} ${altura * 0.74}" ` +
      `fill="none" stroke="${linha}" stroke-width="0.2"/>`,

    CONFETE:
      `<circle cx="3.2" cy="4.4" r="0.8" fill="${principal}"/>` +
      `<circle cx="15.5" cy="2.8" r="0.55" fill="${destaque}"/>` +
      `<rect x="8.6" y="10.4" width="1.5" height="1.5" rx="0.45" fill="${claro}"/>` +
      `<circle cx="19.4" cy="13.6" r="0.7" fill="${destaque}"/>` +
      `<rect x="1.4" y="14.2" width="1.2" height="1.2" rx="0.35" fill="${destaque}" transform="rotate(20 2 14.8)"/>`,
  };

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${largura}" height="${altura}" ` +
    `viewBox="0 0 ${largura} ${altura}">${conteudo[tipo]}</svg>`;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
